import { Suspense, useEffect } from "react";
import { Box, ListItemIcon, ListItemText, MenuItem, MenuList, Stack } from "@mui/material";
import type { ReactNode } from "react";
import type { Theme } from "@mui/material/styles";

import { Hub } from "aws-amplify/utils";
import { signOut } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { ReactRouterAppProvider } from "@toolpad/core/react-router";
import { Outlet, useNavigate } from "react-router-dom";
import { Navigation, NavigationPageItem } from "@toolpad/core/AppProvider";
import { DashboardHeaderProps, DashboardLayout, SidebarFooterProps } from "@toolpad/core/DashboardLayout";

import { Loader } from "@components/Loader";
import { RootState, store } from "@store/store";
import { PopupManager } from "@components/PopupManager";
import { DialogManager } from "@components/DialogManager";
import { setExpandStateLayoutSidebar } from "@store/slices/componentsSlice";
import { BackOfficeTheme } from "@themes/BackOfficeTheme.ts";
import { CustomHeader } from "@components/CustomHeader.tsx";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

interface BaseLayoutProps {
    navigation: Navigation;
    theme?: Theme;
    renderPageItem?: (item: NavigationPageItem, options: { mini: boolean }) => ReactNode;
}

export const BaseLayout = ({ navigation, theme = BackOfficeTheme, renderPageItem }: BaseLayoutProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const userSession = useSelector((state: RootState) => state.session.user);

    useEffect(() => {
        const unsubscribe = Hub.listen("auth", ({ payload }) => {
            switch (payload.event) {
                case "tokenRefresh_failure":
                    dispatch({ type: "CLEAN_STORE" });
                    navigate("/");
                    break;
            }
        });
        return () => unsubscribe();
    }, [dispatch, navigate]);

    const sidebarFooter = (props: SidebarFooterProps) => (
        <Stack direction="column">
            <MenuList>
                <MenuItem
                    onClick={() =>
                        dispatch(
                            setExpandStateLayoutSidebar(
                                !store.getState().components.layout.sidebar.expandState,
                            ),
                        )
                    }
                >
                    <ListItemText>{props.mini ? "" : "Ocultar menú"}</ListItemText>
                    <ListItemIcon>{props.mini ? <MenuIcon /> : <MenuOpenIcon />}</ListItemIcon>
                </MenuItem>
            </MenuList>
        </Stack>
    );

    const handleLogout = async () => {
        dispatch({ type: "CLEAN_STORE" });
        await signOut();
        navigate("/");
    };

    const customHeader = (props: DashboardHeaderProps) => <CustomHeader {...props} />;

    return (
        <ReactRouterAppProvider
            theme={theme}
            navigation={navigation}
            session={{
                user: {
                    name: userSession.name?.concat(
                        " ",
                        userSession.last_name ?? "",
                        " ",
                        userSession.last_name_2 ?? "",
                    ),
                },
            }}
            authentication={{
                signOut: handleLogout,
                signIn: () => {},
            }}
        >
            <DashboardLayout
                hideNavigation={false}
                {...(renderPageItem ? { renderPageItem } : {})}
                sidebarExpandedWidth={220}
                slots={{
                    sidebarFooter,
                    header: customHeader,
                }}
                sx={{ backgroundColor: "#f1f2f7" }}
            >
                <Suspense fallback={<Loader />}>
                    <Box
                        id="content"
                        sx={{
                            py: 3,
                            px: 4,
                            height: { xs: "auto", lg: "100vh" },
                            minHeight: { xs: "100vh", lg: "unset" },
                        }}
                    >
                        <Outlet />
                    </Box>
                </Suspense>
            </DashboardLayout>
            <DialogManager />
            <PopupManager />
        </ReactRouterAppProvider>
    );
};
